import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
import { createStore } from 'vuex';
import _ from 'lodash';
import users from './modules/users';
import carriers from './modules/carriers';
import onboardingPacket from './modules/onboardingPacket';
import authorizations from './modules/authorizations';
import contacts from './modules/contacts';
import factors from './modules/factors';
import payments from './modules/payments';
import insurance from './modules/insurance';
import questions from './modules/questions';
import taxQuestions from './modules/taxQuestions';
import dispatchers from './modules/dispatchers';
import identity from './modules/identity';
import verifications from './modules/verifications';
import editRequest from './modules/editRequest';
import dispatcherConnections from './modules/dispatcherConnections';
import userCompanyRequests from './modules/userCompanyRequests';
import loading from './modules/loading';
import company from './modules/company';
import brokers from './modules/brokers';
import userInvitations from './modules/userInvitations';
import identifiers from './modules/identifiers';
import insuranceExclusions from './modules/insuranceExclusions.js';
import errors from './modules/errors.js';
import integrations from './modules/integrations';
import googleMaps from './modules/googleMaps';
import formRoutes from './modules/formRoutes';
import firstTime from './modules/firstTime';
import rates from './modules/rates';
import show from './modules/show';
import phones from './modules/phones';
import emails from './modules/emails';
import domains from './modules/domains';
import globalSearch from './modules/globalSearch';
import identityVerification from './modules/identityVerification';
import identityAlerts from './modules/identityAlerts';
import laneSearches from './modules/laneSearches';
import ownership from './modules/ownership.js';
import loads from './modules/loads.js';
import myLoadsStore from './modules/myLoadsStore.js';
import toast from './modules/customToast.js';
import legalAgreements from './modules/legalAgreements';
import activities from './modules/activities.js';

export default createStore({
  state: {
    debug: false,
    formDebugStart: null,
    formDebugMessages: [],
    submitButtonDisabled: false,
    submitButtonDisabledSources: [],
    // firstTime: {
    //   Identity: true,
    //   Operations: true,
    //   Lanes: true,
    //   Certifications: true,
    //   Insurance: true,
    //   Payment: true,
    //   Questions: true,
    //   Contract: true
    // },
    integrationsStep: false,
    signUpData: {
      phone_number: '',
      first_name: '',
      last_name: '',
      company_verification_email: 'admin@dhlfreight.com',
      phone_twilio_validation: '',
      full_name: '',
      company_verification_type: '',
      account_verification: '',
      company_email: '',
      email_address: '',
      company_type: '',
    },
    isLogin: false,
    showNeedHelpModal: false,
    showErrorModal: false,
    totpData: {
      qrCode: '',
      setupKey: '',
    },
    totpVerificationResponse: null,
  },
  getters: {
    shouldEnableCHRFlow: ({ users, carriers }) => {
      const isCHROnboardingEnabled =
        users.featureFlags?.enable_chr_onboarding?.enabled;
      const issuerId = String(carriers.carrierData?.connection?.issuer?.id);
      const validIssuers = [
        String(import.meta.env.VITE_CHR),
        String(import.meta.env.VITE_LOADPAY),
      ];

      return isCHROnboardingEnabled && validIssuers.includes(issuerId);
    },
    isForeignCarrier({ carriers }) {
      const identity = carriers?.carrierData?.identity;
      if (!identity) return false;

      const { mailing_address, physical_address, has_foreign_identifier } =
        identity;
      const mailingNationality = mailing_address?.nationality;
      const physicalNationality = physical_address?.nationality;

      const hasNoUSAddress =
        mailingNationality !== 'US' && physicalNationality !== 'US';

      return has_foreign_identifier || hasNoUSAddress;
    },
  },
  mutations: {
    setDebug(state, bool) {
      state.debug = bool;
      console.log('Debug mode set to: ', bool);
    },
    toggleDisabledButton(state, { bool, source }) {
      state.submitButtonDisabled = bool;
      const text = bool ? 'DISABLED' : 'ENABLED';
      const order = state.submitButtonDisabledSources?.length + 1;
      const msg = `${source} | submitButtonDisabled Change #${order}: ${text} by ${source}`;
      state.formDebugMessages.push(msg);
      state.submitButtonDisabledSources.push(msg);
    },
    updateField(state, { name, value }) {
      state.carriers.carrierData[name] = value;
    },
    updateSignupField(state, { name, value }) {
      state.signUpData[name] = value;
    },
    // updateStepFirstTime(state, {step, value}) {
    //   state.firstTime[step] = value;
    // },
    // updateFirstTime(state, value) {
    //   state.firstTime = value;
    // },
    isIntegrationsStep(state, bool) {
      state.integrationsStep = bool;
    },
    updateNestedField(state, { path, value }) {
      _.set(state.carriers.carrierData, path, value);
    },
    setIsLogin(state, bool) {
      state.isLogin = bool;
    },
    setShowNeedHelpModal(state, bool) {
      state.showNeedHelpModal = bool;
    },
    setShowErrorModal(state, bool) {
      state.showErrorModal = bool;
    },
    addFormDebugMessage(state, message) {
      if (state.formDebugStart === null) {
        state.formDebugStart = dayjs().format('x');
        // state.formDebugMessages.push('BEGINNING SESSION: ONBOARDING PACKET ID '+rootState?.onboardingPacket?.onboarding_id);
      }
      let time = (dayjs().format('x') - state.formDebugStart) / 1000;
      let msg = `${message} [logged at ${time}s]`;
      if (state.debug) console.log(msg);
      state.formDebugMessages.push(msg);
    },
    setTOTPData(state, { qrCode, setupKey }) {
      state.totpData.qrCode = qrCode;
      state.totpData.setupKey = setupKey;
    },
    setTOTPVerificationResponse(state, response) {
      state.totpVerificationResponse = response;
    },
    clearTOTPVerificationResponse(state) {
      state.totpVerificationResponse = null;
    },
  },
  actions: {},
  modules: {
    formRoutes,
    firstTime,
    authorizations,
    brokers,
    carriers,
    company,
    contacts,
    dispatcherConnections,
    dispatchers,
    editRequest,
    factors,
    insurance,
    identity,
    loading,
    onboardingPacket,
    payments,
    questions,
    taxQuestions,
    userCompanyRequests,
    userInvitations,
    users,
    verifications,
    identifiers,
    insuranceExclusions,
    errors,
    integrations,
    googleMaps,
    rates,
    show,
    emails,
    phones,
    domains,
    globalSearch,
    identityVerification,
    identityAlerts,
    laneSearches,
    ownership,
    loads,
    myLoadsStore,
    toast,
    legalAgreements,
    activities,
  },
});
